// QR Kiosk v2 — KV2-3 the ONE date+time merge field (bug #7 kill) + platform
// date display (kiosk review §4). A single `Date | undefined` value with two
// projections (date + time). Merge logic lives here ONCE — editing time can
// never clobber the date and vice-versa. Used by BOTH openDate-in-create and
// closeDate-in-solve. No second Controller anywhere.
//
// DISPLAY (owner ask §4): native date/time inputs render in the BROWSER locale
// (mm/dd/yyyy, 12h AM/PM) and cannot be format-forced. So the native inputs are
// kept for their pickers + value semantics but rendered INVISIBLE (opacity 0,
// stretched over the box); the visible text is our own mono `DD.MM.YYYY` /
// 24h `HH:mm` projection — the platform standard (REPORT_DATE_FORMAT).
import React from "react";
import moment from "moment";
import useTranslation from "../../../shared/hooks/useTranslation";
import { localizeDateFormat } from "../shared/dateFormat";
import { FLabel, inputStyle } from "./formAtoms";

export interface KDateTimeFieldProps {
  value?: Date; // the SINGLE merged value (RHF Controller field.value)
  onChange: (v: Date | undefined) => void;
  dateLabel: string;
  timeLabel: string;
  min?: Date; // closeDate: openDate lower bound (attr only; validation in RHF rules)
  disableFuture?: boolean; // max = today on the date input
  error?: boolean; // red border on BOTH inputs
}

const EM_DASH = "—";

// Invisible native input stretched over the styled box — taps land on it, so the
// native picker opens (phone wheel / desktop calendar). fontSize 16 kills the
// iOS focus-zoom even though the input is invisible.
const nativeOverlay: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  border: "none",
  padding: 0,
  margin: 0,
  fontSize: 16,
  cursor: "pointer",
};

// Chromium ≥99 / Safari ≥16 open the picker programmatically on tap; older
// browsers keep the default focus behavior (still functional, just quieter).
function openPicker(e: React.MouseEvent<HTMLInputElement>) {
  const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
  try {
    el.showPicker?.();
  } catch {
    /* needs a user gesture / unsupported — native focus still applies */
  }
}

interface DisplayBoxProps {
  display?: string;
  error?: boolean;
  children: React.ReactNode; // the invisible native input
}
function DisplayBox({ display, error, children }: DisplayBoxProps) {
  return (
    <div style={{ ...inputStyle(error), position: "relative", display: "flex", alignItems: "center" }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 15,
          fontWeight: 600,
          color: display ? "var(--ink)" : "var(--faint)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {display || EM_DASH}
      </span>
      {children}
    </div>
  );
}

export function KDateTimeField({ value, onChange, dateLabel, timeLabel, min, disableFuture, error }: KDateTimeFieldProps) {
  const { i18n } = useTranslation("public");
  const base = () => value ?? new Date(); // no value yet → merge onto now

  const onDate = (iso: string) => {
    if (!iso) return onChange(undefined); // clearing the DATE clears the whole value
    const m = moment(iso, "YYYY-MM-DD");
    onChange(moment(base()).set({ year: m.year(), month: m.month(), date: m.date() }).toDate());
  };
  const onTime = (hhmm: string) => {
    if (!hhmm) return; // clearing TIME alone is a no-op (keeps last valid)
    const [h, mm] = hhmm.split(":").map(Number);
    onChange(moment(base()).set({ hour: h, minute: mm, second: 0, millisecond: 0 }).toDate());
  };

  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div style={{ flex: 1.3 }}>
        <FLabel>{dateLabel}</FLabel>
        <DisplayBox display={value ? moment(value).format(localizeDateFormat("DD.MM.YYYY", i18n.language)) : undefined} error={error}>
          <input
            type="date"
            aria-label={dateLabel}
            value={value ? moment(value).format("YYYY-MM-DD") : ""}
            min={min ? moment(min).format("YYYY-MM-DD") : undefined}
            max={disableFuture ? moment().format("YYYY-MM-DD") : undefined}
            onChange={(e) => onDate(e.target.value)}
            onClick={openPicker}
            style={nativeOverlay}
          />
        </DisplayBox>
      </div>
      <div style={{ flex: 1 }}>
        <FLabel>{timeLabel}</FLabel>
        <DisplayBox display={value ? moment(value).format("HH:mm") : undefined} error={error}>
          <input
            type="time"
            aria-label={timeLabel}
            value={value ? moment(value).format("HH:mm") : ""}
            onChange={(e) => onTime(e.target.value)}
            onClick={openPicker}
            style={nativeOverlay}
          />
        </DisplayBox>
      </div>
    </div>
  );
}

export default KDateTimeField;
